import { Repeater } from '@repeaterjs/repeater';
import _ from 'lodash';
import BTree from 'sorted-btree';

import type { RepeaterBuffer, RepeaterExecutor } from '@repeaterjs/repeater';

// NOTE: this is a version of RepeaterPipeline from api-v2 with the features specific to backend and
//       scripting removed (specifically the .fromCursor() constructor, Cloud Run worker stream,
//       and .withProgress())
//
//       If/when we try to integrate api-v2 into the monorepo then we should probably try to figure
//       out a way to configure a more extensible/modular version of this to allow the base subset
//       of features to be used in the frontend

export interface RepeaterPipelineCreateOptions {
    buffer?: RepeaterBuffer;
    stopOnError?: boolean;
}

/** Extended version of {@link Repeater} with "pipeline style" helper methods such as filter, map, etc */
export class RepeaterPipeline<T> extends Repeater<T> {
    private readonly stopOnError: boolean;

    constructor(
        executor: RepeaterExecutor<T>,
        { buffer, stopOnError = true }: RepeaterPipelineCreateOptions = {},
    ) {
        super(executor, buffer);
        this.stopOnError = stopOnError;
    }

    /**
     * Helper method to create a child pipeline with the same options as this one.
     */
    protected createChild<U>(executor: RepeaterExecutor<U>): RepeaterPipeline<U> {
        return new RepeaterPipeline<U>(
            async (push, stop) => {
                try {
                    await executor(push, stop);
                } catch (err) {
                    stop(err);
                    if (this.stopOnError) {
                        void this.return();
                    }
                }
            },
            { stopOnError: this.stopOnError },
        );
    }

    /**
     * Creates a new {@link RepeaterPipeline} stream from an array.
     *
     * @param array - the array to map to a stream
     */
    static fromArray<T>(array: T[]): RepeaterPipeline<T> {
        return RepeaterPipeline.fromIterable(array);
    }

    /**
     * Creates a new {@link RepeaterPipeline} stream from an iterable collection.
     *
     * @param iterator - the iterable/collection to map to a stream
     */
    static fromIterable<T>(
        iterator: IterableIterator<T> | Iterable<T>,
        opts?: RepeaterPipelineCreateOptions,
    ): RepeaterPipeline<T> {
        return new RepeaterPipeline<T>(async (push, stop) => {
            for (const item of iterator) {
                await push(item);
            }
            stop();
        }, opts);
    }

    /**
     * Buffers upstream results, which is useful for ensuring that RepeaterPipeline.fromCursor() does not lose its
     * cursor/query connection due to a slow/long-running downstream process.  (Note that due to the buffering behavior
     * for best performance, order of query results is not guaranteed.)
     *
     * @param onCompleted - optional handler that is called once all upstream results have been fully received/buffered
     *                      with the total count of query results
     */
    buffered(onCompleted?: (total: number) => void): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            let total = 0;
            const buffer: T[] = [];
            let finished = false;
            let err: unknown = null;

            const resetBufferNotEmptyPromise = () => {
                bufferNonEmptyPromise = new Promise<void>((resolve, reject) => {
                    signalBufferNotEmpty = resolve;
                    rejectBufferNotEmpty = reject;
                });
            };

            let bufferNonEmptyPromise: Promise<void> = null!;
            let signalBufferNotEmpty: () => void = null!;
            let rejectBufferNotEmpty: (e: unknown) => void = null!;
            resetBufferNotEmptyPromise();

            void this.forEach(item => {
                buffer.push(item);
                total++;
                if (buffer.length === 1) {
                    signalBufferNotEmpty();
                }
            })
                .then(() => {
                    finished = true;
                    onCompleted?.(total);
                })
                .catch(e => {
                    err = e;
                    rejectBufferNotEmpty(e);
                });

            while (!err && (!finished || buffer.length > 0)) {
                if (buffer.length > 0) {
                    const item = buffer.pop()!;
                    await push(item);
                } else {
                    // Promise.race against a timeout promise as a hedge against some race condition slipping through
                    await Promise.race([
                        bufferNonEmptyPromise,
                        new Promise(resolve => setTimeout(resolve, 30_000)),
                    ]);
                    resetBufferNotEmptyPromise();
                }
            }

            if (err) throw err;
            stop();
        });
    }

    /**
     * Helper method to work around the overly narrow mongoose cursor typing. Will cast a RepeaterPipeline of
     * one type to a RepeaterPipeline of a different type.
     */
    as<TCast>(): RepeaterPipeline<TCast> {
        return this as unknown as RepeaterPipeline<TCast>;
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream that emits only items for which the
     * given predicate evaluates to true.
     *
     * @param predicate - a function returning a boolean to indicate whether an item should be included in the wrapped
     *                    stream
     * @param onFiltered - optional handler for items that are filtered out of the stream
     *                     (useful for e.g. logging skipped items)
     */
    filter(
        predicate: (val: T) => boolean | Promise<boolean>,
        onFiltered?: (val: T) => unknown,
    ): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            for await (const item of this) {
                if (!(await predicate(item))) {
                    if (onFiltered) await onFiltered(item);
                    continue;
                }
                await push(item);
            }
            stop();
        });
    }

    /**
     * A variant of {@link RepeaterPipeline.filter()} that accepts a type guard as a predicate and narrows the typing
     * of the resulting stream to match the type guard.
     *
     * @param predicate - a function returning a boolean to indicate whether an item should be included in the wrapped
     *                    stream
     */
    filterNarrow<TNarrow extends T>(
        predicate: (val: T) => val is TNarrow,
    ): RepeaterPipeline<TNarrow> {
        return this.filter(predicate) as RepeaterPipeline<TNarrow>;
    }

    /** Collects all items emitted by this stream into an array. */
    async toArray(): Promise<T[]> {
        const result: T[] = [];
        await this.forEach(item => {
            result.push(item);
        });
        return result;
    }

    /** Collects all items into a map */
    async toMap<TKey, TValue = T>(
        getKey: (val: T) => TKey,
        getValue: (val: T) => TValue = x => x as unknown as TValue,
    ): Promise<Map<TKey, TValue>> {
        const result = new Map<TKey, TValue>();

        for await (const item of this) {
            result.set(getKey(item), getValue(item));
        }

        return result;
    }

    async toGroupedMap<TKey, TVal = T>(
        getKey: (val: T) => TKey,
        getValue: (val: T) => TVal = v => v as unknown as TVal,
    ): Promise<Map<TKey, TVal[]>> {
        const result = new Map<TKey, TVal[]>();

        for await (const item of this) {
            const k = getKey(item);

            let bucket = result.get(k);
            if (!bucket) {
                bucket = [];
                result.set(k, bucket);
            }
            bucket.push(getValue(item));
        }

        return result;
    }

    groupBy<TKey>(getKey: (val: T) => TKey): RepeaterPipeline<[TKey, T[]]> {
        return this.createChild<[TKey, T[]]>(async (push, stop) => {
            const map = await this.toGroupedMap(getKey);
            for (const entry of map) {
                await push(entry);
            }
            stop();
        });
    }

    /** Collects all items into a set */
    async toSet(): Promise<Set<T>> {
        const result = new Set<T>();
        await this.forEach(item => {
            result.add(item);
        });
        return result;
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which collects all the items and then re-emits
     * them in an order of given sort key. Because of the intermediate collection type being used the resulting stream
     * will be unique on the sort key
     *
     * @param sortKey - a callback function that returns a string value to sort/uniq by
     */
    sortedUniq(sortKey: (item: T) => string): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            const btree = new BTree<string, T>();

            await this.forEach(item => {
                btree.set(sortKey(item), item);
            });

            for (const value of btree.values()) {
                await push(value);
            }
            stop();
        });
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which collects all the items and then re-emits
     * them in descending order of given sort key. Because of the intermediate collection type being used the resulting
     * stream will be unique on the sort key
     *
     * @param sortKey - a callback function that returns a string value to sort/uniq by
     */
    sortedUniqDesc(sortKey: (item: T) => string): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            const btree = new BTree<string, T>(undefined, (a, b) => (a > b ? -1 : a < b ? 1 : 0));

            await this.forEach(item => {
                btree.set(sortKey(item), item);
            });

            for (const value of btree.values()) {
                await push(value);
            }
            stop();
        });
    }

    /**
     * Apply an arbitrary transformation to this {@link RepeaterPipeline}.
     *
     * @param operator - a callback function that takes in the current pipeline and returns a wrapped version of
     *                   the pipeline
     */
    transform<TOut>(
        operator: (input: RepeaterPipeline<T>) => RepeaterPipeline<TOut>,
    ): RepeaterPipeline<TOut> {
        return operator(this);
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which transforms all emitted items through
     * a mapping function.
     *
     * @param mapper - a function that transforms a single item
     */
    map<TMapped>(
        mapper: (val: T, index: number) => TMapped | Promise<TMapped>,
    ): RepeaterPipeline<TMapped> {
        return this.createChild<TMapped>(async (push, stop) => {
            let index = 0;

            for await (const item of this) {
                await push(await mapper(item, index));
                index++;
            }
            stop();
        });
    }

    skip(count: number): RepeaterPipeline<T> {
        let index = 0;

        return this.skipWhile(() => {
            if (index < count) {
                index++;
                return true;
            }
            return false;
        });
    }

    skipWhile(predicate: (val: T) => boolean | Promise<boolean>): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            let skip = true;

            for await (const item of this) {
                if (skip && !(await predicate(item))) {
                    skip = false;
                }
                await push(item);
            }
            stop();
        });
    }

    take(count: number): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            let index = 0;

            for await (const item of this) {
                if (index < count) {
                    await push(item);
                } else {
                    await this.return(); // Tell the upstream repeater to stop processing
                    break;
                }
                index++;
            }
            stop();
        });
    }

    concat<TConcat extends T = T>(
        sequence: Iterable<TConcat> | AsyncIterable<TConcat>,
    ): RepeaterPipeline<T> {
        return this.createChild<T>(async (push, stop) => {
            for await (const item of this) {
                await push(item);
            }

            for await (const item of sequence) {
                await push(item);
            }

            stop();
        });
    }

    /**
     * A variant {@link RepeaterPipeline.map} which allows multiple async mapping operations to be outstanding at
     * a given time and does not guarantee stream ordering. (This method does not directly execute
     * multiple actions in parallel; if you are wanting to parallelize a CPU-bound task it is recommended
     * to stand up a Cloud Run worker and use {@link RepeaterPipeline.toCloudRunStream} instead.)
     *
     * @param parallelCount - the number of allowed async operations at a given time
     * @param mapper - an async function that transforms a single item
     */
    mapParallel<TMapped>(
        parallelCount: number,
        mapper: (val: T) => Promise<TMapped>,
    ): RepeaterPipeline<TMapped> {
        return this.createChild<TMapped>(async (push, stop) => {
            const deferreds: Map<string, PromiseState<TMapped>> = new Map();

            const addDeferred = (promise: Promise<TMapped>) => {
                const deferred = new PromiseState(promise);
                deferreds.set(deferred.id, deferred);
            };

            const waitForDeferredToResolve = () =>
                deferreds.size
                    ? Promise.race([...deferreds.values()].map(x => x.promise))
                    : Promise.resolve();

            const emitResolvedDeferreds = async () => {
                for (const deferred of deferreds.values()) {
                    if (deferred.resolved) {
                        deferreds.delete(deferred.id);
                        await push(deferred.result!);
                    } else if (deferred.rejected) {
                        throw deferred.error;
                    }
                }
            };

            await this.forEachInterruptible({
                beforeNext: async (nextPromise, nextResolved) => {
                    while (deferreds.size > 0 && !nextResolved()) {
                        await Promise.race([nextPromise, waitForDeferredToResolve()]);
                        if (!nextResolved()) {
                            await emitResolvedDeferreds();
                        }
                    }
                },
                itemHandler: async value => {
                    addDeferred(mapper(value));

                    await emitResolvedDeferreds();
                    while (deferreds.size >= parallelCount) {
                        await waitForDeferredToResolve();
                        await emitResolvedDeferreds();
                    }
                },
            });

            while (deferreds.size > 0) {
                await waitForDeferredToResolve();
                await emitResolvedDeferreds();
            }

            stop();
        });
    }

    /**
     * A variant of {@link RepeaterPipeline.map} that accepts a mapping function that returns an array or stream
     * and emits all items in each list/stream individually.
     *
     * @param mapper - a mapping function that returns a list or stream of items to emit individually from the
     *                 resulting stream
     */
    flatMap<TItem>(
        mapper: (val: T) => TItem[] | Promise<TItem[]> | Repeater<TItem> | Promise<Repeater<TItem>>,
    ): RepeaterPipeline<TItem> {
        return this.createChild<TItem>(async (push, stop) => {
            for await (const item of this) {
                const chunk = await mapper(item);
                for await (const innerItem of chunk) {
                    await push(innerItem);
                }
            }
            stop();
        });
    }

    /**
     * A variant of {@link RepeaterPipeline.flatMap} which allows multiple outstanding async mapping operations.
     * (This method does not directly execute multiple actions in parallel; if you are wanting to parallelize a
     * CPU-bound task it is recommended to stand up a Cloud Run worker and use
     * {@link RepeaterPipeline.toCloudRunWorkerStream} instead.)
     *
     * @param parallelCount - the number of async operations that are allowed to be outstanding at a given time
     * @param mapper - a mapping function that returns a list or stream of items to emit individually from the
     *                 resulting stream
     */
    flatMapParallel<TItem>(
        parallelCount: number,
        mapper: (val: T) => Promise<TItem[] | Repeater<TItem>>,
    ): RepeaterPipeline<TItem> {
        const parallelStream = this.mapParallel(parallelCount, mapper);

        return this.createChild<TItem>(async (push, stop) => {
            for await (const list of parallelStream) {
                for await (const item of list) {
                    await push(item);
                }
            }

            stop();
        });
    }

    /**
     * Applies a reducer function to all items in the stream then returns the result.
     *
     * @param reducer - A reducer function that accepts the previous value and the current item and returns
     *                  an updated value.
     * @param initialValue - starting value to pass into the reducer function the first time
     */
    async reduce<R>(
        reducer: (previousValue: R, currentValue: T) => R | Promise<R>,
        initialValue: R,
    ): Promise<R> {
        let value = initialValue;

        for await (const item of this) {
            value = await reducer(value, item);
        }

        return value;
    }

    /**
     * Executes an action on all items in a stream and then returns.
     *
     * @param handler - the action to take for each item in the stream
     */
    async forEach(handler: (val: T) => void | Promise<void>): Promise<void> {
        try {
            for await (const item of this) {
                await handler(item);
            }
        } catch (err) {
            if (this.stopOnError) {
                void this.return();
            }
            throw err;
        }
    }

    /**
     * A variant of {@link RepeaterPipeline.forEach} that allows multiple items/actions to be outstanding at a given
     * time. (This method does not directly execute multiple actions in parallel; if you are wanting to parallelize a
     * CPU-bound task it is recommended to stand up a Cloud Run worker and use
     * {@link RepeaterPipeline.toCloudRunStream} instead.)
     *
     * @param parallelCount - the number of async operations that are allowed to be outstanding at a given time
     * @param handler - the action to take for each item in the stream
     */
    async forEachParallel(
        parallelCount: number,
        handler: (val: T) => Promise<void>,
    ): Promise<void> {
        await this.mapParallel(parallelCount, async val => {
            await handler(val);
            return null;
        }).execute();
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which executes a handler on each item of
     * the incoming stream before emitting that item to any downstream consumers.
     *
     * @param handler - the action to execute on each item in the stream
     */
    onEach(handler: (item: T) => void | Promise<void>): RepeaterPipeline<T> {
        return this.map(async val => {
            await handler(val);
            return val;
        });
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which executes a handler on each item of
     * the incoming stream before emitting that item to any downstream consumers, and allows multiple outstanding
     * operations at a time (note this is not true parallelism but rather multiplexing async operations on the
     * same thread).
     *
     * @param parallelCount - the number of allowed async operations at a given time
     * @param handler - the action to execute on each item in the stream
     */
    onEachParallel(parallelCount: number, handler: (val: T) => Promise<void>): RepeaterPipeline<T> {
        return this.mapParallel(parallelCount, async val => {
            await handler(val);
            return val;
        });
    }

    /** Immediately iterates through all items in a stream without taking any additional action. */
    async execute(): Promise<void> {
        await this.forEach(() => {});
    }

    /**
     * Returns a wrapped version of this {@link RepeaterPipeline} stream which emits items in chunked arrays. This
     * method accepts a maximum chunk size and a timeout (which defaults to 10 seconds).
     *
     * @param options
     * @param options.chunkSize - The maximum number of items to emit in each chunk
     * @param options.timeout - The number of milliseconds to wait before emitting a chunk if no new items have come
     *                          in. Defaults to 10 seconds.
     */
    chunked({
        chunkSize,
        timeout = 10_000,
    }: {
        chunkSize: number;
        timeout?: number;
    }): RepeaterPipeline<T[]> {
        return this.createChild<T[]>(async (push, stop) => {
            let lastEmitTime = 0;
            let timeoutExpire = 0;
            const buffer: T[] = [];

            const updateTimeoutExpire = () => {
                lastEmitTime = Date.now();
                timeoutExpire = lastEmitTime + timeout;
            };

            const emitChunk = async () => {
                const chunk: T[] = [];
                while (buffer.length > 0 && chunk.length < chunkSize) {
                    chunk.push(buffer.pop()!);
                }
                await push(chunk);
                updateTimeoutExpire();
            };

            await this.forEachInterruptible({
                beforeNext: async nextPromise => {
                    if (!buffer.length) return;

                    const now = Date.now();
                    if (now >= timeoutExpire) {
                        await emitChunk();
                    } else {
                        await Promise.race([
                            nextPromise,
                            new Promise(resolve => setTimeout(resolve, timeoutExpire - now)),
                        ]);
                        if (Date.now() >= timeoutExpire) {
                            await emitChunk();
                        }
                    }
                },
                itemHandler: async value => {
                    if (!timeoutExpire && !buffer.length) updateTimeoutExpire();
                    buffer.push(value);

                    if (buffer.length >= chunkSize || Date.now() > timeoutExpire) {
                        await emitChunk();
                    }
                },
            });

            if (buffer.length) {
                await emitChunk(); // catch any remaining items
            }
            stop();
        });
    }

    async count(): Promise<number> {
        let result = 0;
        await this.forEach(() => {
            result++;
        });
        return result;
    }

    /**
     * A low-level helper method to facilitate parallel streaming operations.
     * It is similar to {@link RepeaterPipeline.forEach} but in addition to a per-item handler callback it also
     * accepts a callback that it calls while waiting to receive the next item the stream.
     *
     * @param options
     * @param options.itemHandler - A callback function that is called for each item that is received
     * @param options.beforeNext - A callback function that is called while
     *                             waiting for a new item to be emitted into the stream.
     */
    async forEachInterruptible({
        itemHandler,
        beforeNext,
    }: {
        itemHandler: (val: T) => Promise<void>;
        // can't get callback jsdoc comment to register correctly with IntelliJ, but here it is inline at least
        /**
         * @callback BeforeNextCallback
         * This callback is called by {@link RepeaterPipeline.forEachInterruptible} while waiting for items to be emitted
         * into the stream.
         *
         * @param nextPromise - A promise which resolves when a new item has been emitted into the stream. This is
         *                      typically passed into {@link Promise.race} along with any other outstanding promises.
         * @param nextResolved - A callback function with returns true if a new item has been emitted into the stream.
         */
        beforeNext: (nextPromise: Promise<void>, nextResolved: () => boolean) => Promise<void>;
    }): Promise<void> {
        let nextResult: IteratorResult<T> | null = null;
        const nextResolved = () => !!nextResult;

        try {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                nextResult = null;
                const nextPromise = this.next().then(r => {
                    nextResult = r;
                });

                await beforeNext(nextPromise, nextResolved);

                if (!nextResult) await nextPromise;
                if (nextResult!.done) break;

                await itemHandler(nextResult!.value);
            }
        } catch (err) {
            await this.throw(err);
            return;
        }

        await this.return();
    }
}

class PromiseState<T> {
    readonly id = _.uniqueId();
    readonly promise: Promise<void>;
    resolved = false;
    result: T | null = null;
    error: any = null;
    rejected = false;

    constructor(promise: Promise<T>) {
        this.promise = promise
            .then(res => {
                this.result = res;
                this.resolved = true;
            })
            .catch(err => {
                this.error = err;
                this.rejected = true;
            });
    }
}
